import { OrderRepository } from '../../domain/repositories/OrderRepository';
import { ProductRepository } from '../../domain/repositories/ProductRepository';
import { FirebaseOrderRepository } from '../repositories/FirebaseOrderRepository';
// Use client-side cached product repository for UI hooks
import { CachedFirebaseProductRepository } from '../repositories/CachedFirebaseProductRepository';
import { SubmitOrderUseCase } from '../../application/use-cases/SubmitOrderUseCase';
import { GetOrderUseCase } from '../../application/use-cases/GetOrderUseCase';
import { GetProductsUseCase } from '../../application/use-cases/GetProductsUseCase';
import { CreateProductUseCase } from '../../application/use-cases/CreateProductUseCase';
import { OrderService } from '../services/OrderService';
import { ProductService } from '../services/ProductService';
import { CartService } from '../../application/services/CartService';
import { LocalStorageCartRepository } from '../repositories/LocalStorageCartRepository';

export class ApplicationServiceContainer {
  private static containerInstance: ApplicationServiceContainer;
  private readonly registeredServices: Map<string, unknown> = new Map();

  private constructor() {
    this.initializeAllServices();
  }

  static getSharedInstance(): ApplicationServiceContainer {
    if (!ApplicationServiceContainer.containerInstance) {
      ApplicationServiceContainer.containerInstance = new ApplicationServiceContainer();
    }
    return ApplicationServiceContainer.containerInstance;
  }

  private initializeAllServices(): void {
    this.registerRepositoryImplementations();
    this.registerUseCaseImplementations();
    // Service facades will be created lazily when requested
  }

  private registerRepositoryImplementations(): void {
    this.registeredServices.set('OrderRepository', new FirebaseOrderRepository());
    this.registeredServices.set('ProductRepository', new CachedFirebaseProductRepository());
    // Client-side cart persistence repository
    this.registeredServices.set('CartRepository', new LocalStorageCartRepository());
  }

  private registerUseCaseImplementations(): void {
    this.registeredServices.set(
      'SubmitOrderUseCase',
      new SubmitOrderUseCase(
        this.resolveService<OrderRepository>('OrderRepository'),
        this.resolveService<ProductRepository>('ProductRepository'),
      ),
    );

    this.registeredServices.set(
      'GetOrderUseCase',
      new GetOrderUseCase(this.resolveService<OrderRepository>('OrderRepository')),
    );

    this.registeredServices.set(
      'GetProductsUseCase',
      new GetProductsUseCase(this.resolveService<ProductRepository>('ProductRepository')),
    );

    this.registeredServices.set(
      'CreateProductUseCase',
      new CreateProductUseCase(this.resolveService<ProductRepository>('ProductRepository')),
    );
  }

  resolveService<TService>(serviceIdentifier: string): TService {
    let resolvedService = this.registeredServices.get(serviceIdentifier);

    // Lazy initialization for service facades
    if (!resolvedService) {
      if (serviceIdentifier === 'OrderService') {
        resolvedService = new OrderService();
        this.registeredServices.set('OrderService', resolvedService);
      } else if (serviceIdentifier === 'ProductService') {
        resolvedService = new ProductService();
        this.registeredServices.set('ProductService', resolvedService);
      } else if (serviceIdentifier === 'CartService') {
        resolvedService = new CartService();
        this.registeredServices.set('CartService', resolvedService);
      } else {
        throw new Error(`Service '${serviceIdentifier}' has not been registered in the container`);
      }
    }

    return resolvedService as TService;
  }

  // Repository access methods
  resolveOrderRepository(): OrderRepository {
    return this.resolveService<OrderRepository>('OrderRepository');
  }

  resolveProductRepository(): ProductRepository {
    return this.resolveService<ProductRepository>('ProductRepository');
  }

  // Cart repository (client-side local storage)
  resolveCartRepository(): LocalStorageCartRepository {
    return this.resolveService<LocalStorageCartRepository>('CartRepository');
  }

  // Use case access methods
  resolveSubmitOrderUseCase(): SubmitOrderUseCase {
    return this.resolveService<SubmitOrderUseCase>('SubmitOrderUseCase');
  }

  resolveGetOrderUseCase(): GetOrderUseCase {
    return this.resolveService<GetOrderUseCase>('GetOrderUseCase');
  }

  resolveGetProductsUseCase(): GetProductsUseCase {
    return this.resolveService<GetProductsUseCase>('GetProductsUseCase');
  }

  resolveCreateProductUseCase(): CreateProductUseCase {
    return this.resolveService<CreateProductUseCase>('CreateProductUseCase');
  }
}
